import React, { createContext, useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';
import { fetchAppointmentStats, fetchSmartSuggestions, warmSmartSuggestions } from 'utils/apiHelper';
import useAuth from 'hooks/useAuth';

const CACHE_KEY = 'preferencesData:v2';
const SMART_ENABLED_KEY = 'smartSuggestionsEnabled';
const CACHE_TTL_MS = 30 * 60 * 1000;

let suggestionsCache = {};
let fetchPromise = null;
let isInitialized = false;
let listeners = [];

const getCachedData = () => {
  try {
    const cachedRaw = sessionStorage.getItem(CACHE_KEY);
    if (!cachedRaw) return null;

    const { data, timestamp } = JSON.parse(cachedRaw);
    const now = Date.now();

    let dataString;
    try {
      if (typeof data === 'string') {
        dataString = data;
      } else if (data && typeof data === 'object') {
        dataString = JSON.stringify(data);
      }
    } catch (e) {
      dataString = undefined;
    }

    const isValidCache =
      data &&
      (typeof data === 'object' || typeof data === 'string') &&
      typeof dataString === 'string' &&
      !dataString.toLowerCase().includes('<!doctype') &&
      !dataString.toLowerCase().includes('<html');

    if (isValidCache && now - timestamp < CACHE_TTL_MS) {
      return data;
    }

    if (!isValidCache) {
      sessionStorage.removeItem(CACHE_KEY);
    }
    return null;
  } catch (e) {
    sessionStorage.removeItem(CACHE_KEY);
    return null;
  }
};

const setCachedData = (data) => {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ data, timestamp: Date.now() })
    );
  } catch (e) {}
};

const initializeSuggestions = () => {
  if (!isInitialized) {
    const cachedData = getCachedData();
    if (cachedData) {
      suggestionsCache = cachedData;
      isInitialized = true;
    }
  }
};

initializeSuggestions();

export const fetchSuggestionsData = async (forceRefresh = false) => {
  if (fetchPromise && !forceRefresh) {
    return fetchPromise;
  }

  if (!forceRefresh) {
    const cachedData = getCachedData();
    if (cachedData) {
      suggestionsCache = cachedData;
      isInitialized = true;
      return cachedData;
    }
  }

  fetchPromise = (async () => {
    try {
      const data = await fetchAppointmentStats();
      const suggestionsData = data || {};
      setCachedData(suggestionsData);
      suggestionsCache = suggestionsData;
      isInitialized = true;
      // Notify all listeners that suggestions have been updated
      listeners.forEach(listener => listener(suggestionsData));
      return suggestionsData;
    } catch (err) {
      sessionStorage.removeItem(CACHE_KEY);
      // Reset in-memory cache on failure
      suggestionsCache = {};
      isInitialized = false;
      // Notify listeners of the empty state
      listeners.forEach(listener => listener({}));
      return {};
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
};

export const getSuggestionsData = () => suggestionsCache;

export const refreshSuggestionsData = () => fetchSuggestionsData(true);

// Subscribe to suggestions updates
const subscribe = (listener) => {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter(l => l !== listener);
  };
};

const SuggestionsContext = createContext(undefined);

export const SuggestionsProvider = ({ children }) => {
  const { isLoggedIn, user } = useAuth();
  const [staticSuggestions, setStaticSuggestions] = useState(() => getSuggestionsData());
  const [smartSuggestions, setSmartSuggestions] = useState(null);
  const [smartSuggestionsEnabled, setSmartSuggestionsEnabledState] = useState(
    () => localStorage.getItem(SMART_ENABLED_KEY) === 'true'
  );
  const [smartSuggestionsMode, setSmartSuggestionsMode] = useState('static');
  const [smartSuggestionsLoading, setSmartSuggestionsLoading] = useState(false);
  const contextItemsRef = useRef([]);
  const requestRef = useRef(null);
  const timerRef = useRef(null);
  const smartCacheRef = useRef(new Map());

  useEffect(() => {
    // Subscribe to suggestions updates
    const unsubscribe = subscribe((newSuggestions) => {
      setStaticSuggestions(newSuggestions);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
      requestRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const isDoctor = Array.isArray(user?.roles) && user.roles.includes('doctor');
    if (isLoggedIn && isDoctor && smartSuggestionsEnabled) {
      warmSmartSuggestions().catch(() => {});
    }
  }, [isLoggedIn, user?.id, user?.roles, smartSuggestionsEnabled]);

  const loadSmartSuggestions = useCallback(async (contextItems) => {
    const fingerprint = contextItems.join('|');
    const cached = smartCacheRef.current.get(fingerprint);
    if (cached && Date.now() - cached.timestamp < 5 * 60 * 1000) {
      setSmartSuggestions(cached.data);
      setSmartSuggestionsMode('smart');
      return;
    }

    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setSmartSuggestionsLoading(true);
    try {
      const data = await fetchSmartSuggestions(contextItems, controller.signal);
      if (data?.mode === 'smart') {
        smartCacheRef.current.set(fingerprint, { data, timestamp: Date.now() });
        setSmartSuggestions(data);
        setSmartSuggestionsMode('smart');
      } else {
        setSmartSuggestions(null);
        setSmartSuggestionsMode('warming');
      }
    } catch (error) {
      if (error?.code !== 'ERR_CANCELED' && error?.name !== 'CanceledError') {
        setSmartSuggestions(null);
        setSmartSuggestionsMode('static');
      }
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
        setSmartSuggestionsLoading(false);
      }
    }
  }, []);

  const setActiveContext = useCallback((contextItems = []) => {
    const normalized = Array.from(new Set(contextItems.filter(Boolean))).sort();
    if (normalized.join('|') === contextItemsRef.current.join('|')) return;
    contextItemsRef.current = normalized;
    clearTimeout(timerRef.current);
    requestRef.current?.abort();
    if (!smartSuggestionsEnabled || normalized.length === 0) {
      setSmartSuggestions(null);
      setSmartSuggestionsMode('static');
      setSmartSuggestionsLoading(false);
      return;
    }
    timerRef.current = setTimeout(() => loadSmartSuggestions(normalized), 250);
  }, [loadSmartSuggestions, smartSuggestionsEnabled]);

  const setSmartSuggestionsEnabled = useCallback(async (enabled) => {
    setSmartSuggestionsEnabledState(enabled);
    localStorage.setItem(SMART_ENABLED_KEY, String(enabled));
    if (!enabled) {
      requestRef.current?.abort();
      setSmartSuggestions(null);
      setSmartSuggestionsMode('static');
    } else if (contextItemsRef.current.length) {
      loadSmartSuggestions(contextItemsRef.current);
    }
    return true;
  }, [loadSmartSuggestions]);

  const suggestions = smartSuggestionsEnabled && smartSuggestions
    ? { ...staticSuggestions, ...smartSuggestions }
    : staticSuggestions;

  const contextValue = useMemo(() => ({
    suggestions,
    fetchSuggestions: fetchSuggestionsData,
    refreshSuggestions: refreshSuggestionsData,
    setActiveContext,
    smartSuggestionsEnabled,
    setSmartSuggestionsEnabled,
    smartSuggestionsLoading,
    smartSuggestionsMode,
  }), [suggestions, setActiveContext, smartSuggestionsEnabled, setSmartSuggestionsEnabled, smartSuggestionsLoading, smartSuggestionsMode]);

  return (
    <SuggestionsContext.Provider value={contextValue}>
      {children}
    </SuggestionsContext.Provider>
  );
};

export const useSuggestions = () => {
  const context = useContext(SuggestionsContext);
  if (!context) {
    throw new Error('useSuggestions must be used within a SuggestionsProvider');
  }
  return context;
};

// Hook that returns the reactive suggestions data
export const useSuggestionsData = () => {
  const { suggestions } = useSuggestions();
  return suggestions;
};

export default SuggestionsContext;
